import {
  ArrowLeft,
  Battery,
  CheckCircle2,
  ClipboardList,
  Clock,
  MapPin,
  Navigation,
  Phone,
  ShieldAlert,
  Timer,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  type AlarmEventDto,
  type AlarmResolutionOutcome,
  useAcknowledgeAlarm,
  useAlarm,
  useResolveAlarm,
} from '../api/useAlarms.js';
import { type RosterMember, useRoster } from '../api/useRoster.js';
import { StatusPill } from '../components/StatusPill.js';
import { cn } from '../lib/cn.js';
import { formatAlarmType, formatDateTime, formatRelative } from '../lib/format.js';

export default function AlarmDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useAlarm(id);
  const roster = useRoster();
  const ack = useAcknowledgeAlarm();
  const resolve = useResolveAlarm();
  const [resolveNote, setResolveNote] = useState('');

  if (isLoading) return <Loading />;
  if (error || !data) return <NotFound />;

  const { alarm, events } = data;

  // Resolve worker + actor ids to people. The roster is loaded app-wide and
  // cached, so this is usually instant; it degrades to the id if not.
  const byId = new Map((roster.data?.members ?? []).map((m) => [m.user_id, m] as const));
  const worker = byId.get(alarm.user_id);
  const nameFor = (uid: string | null): string | null =>
    uid ? (byId.get(uid)?.display_name ?? 'Unknown') : null;

  const triggeredMs = new Date(alarm.triggered_at).getTime();
  const resolvedMs = alarm.resolved_at ? new Date(alarm.resolved_at).getTime() : null;
  const isOpen = alarm.status === 'active' || alarm.status === 'acknowledged';
  const inAlarmMs = (resolvedMs ?? Date.now()) - triggeredMs;

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <Link
        to="/alarms"
        className="inline-flex items-center gap-1 text-sm text-ink-faint hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to alarms
      </Link>

      <div className="panel">
        <div className="panel-body">
          <div className="flex items-start justify-between gap-6">
            <div>
              <span className="eyebrow">Alarm detail</span>
              <div className="mt-1 flex items-center gap-2.5">
                <ShieldAlert
                  className={cn(
                    'h-5 w-5',
                    alarm.type === 'duress' ? 'text-danger' : 'text-warning',
                  )}
                />
                <h1 className="font-heading text-[22px] font-bold tracking-tightest text-ink">
                  {formatAlarmType(alarm.type)} alarm
                </h1>
                <StatusPill status={alarm.status} className="ml-1" />
              </div>
              <div className="mt-1 text-sm text-ink-faint">
                Triggered {formatRelative(alarm.triggered_at)} ·{' '}
                <span className="font-mono text-[12px]">{formatDateTime(alarm.triggered_at)}</span>
              </div>
            </div>

            <div className="flex gap-2">
              {alarm.status === 'active' && (
                <button
                  type="button"
                  onClick={() => ack.mutate(alarm.id)}
                  disabled={ack.isPending}
                  className="rounded-md bg-brand-purple px-4 py-2 text-sm font-semibold text-white shadow-glow-primary transition hover:bg-brand-purple/90 disabled:opacity-50"
                >
                  {ack.isPending ? 'Acknowledging…' : 'Acknowledge'}
                </button>
              )}
              <Link
                to={`/incidents?alarm_id=${alarm.id}&affected_user_id=${alarm.user_id}`}
                className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface-1 px-3 py-2 text-sm font-semibold text-ink-dim transition hover:border-brand-purple hover:text-brand-purple"
                title="Open a formal incident record for this alarm"
              >
                <ClipboardList className="h-4 w-4" />
                Create incident
              </Link>
            </div>
          </div>

          {/* WHO + how to reach them — the first thing a duress response needs. */}
          <WorkerHero worker={worker} battery={alarm.device_battery} />
        </div>

        {/* Response accountability: who ack'd, when resolved, and how long the
            worker sat in the alarm state. Battery moved to the worker row. */}
        <div className="grid grid-cols-3 gap-px bg-line">
          <Fact
            icon={CheckCircle2}
            label="Acknowledged"
            tone={isOpen && !alarm.acknowledged_at ? 'warning' : undefined}
            value={
              alarm.acknowledged_at ? (
                <>
                  {formatRelative(alarm.acknowledged_at)}
                  <div className="text-[11px] font-normal text-ink-faint">
                    by {nameFor(alarm.acknowledged_by) ?? '—'}
                  </div>
                </>
              ) : isOpen ? (
                'Awaiting'
              ) : (
                '—'
              )
            }
          />
          <Fact
            icon={Clock}
            label="Resolved"
            value={
              resolvedMs ? (
                <>
                  {formatRelative(alarm.resolved_at)}
                  <div className="text-[11px] font-normal text-ink-faint">
                    by {nameFor(alarm.resolved_by) ?? 'worker'}
                  </div>
                </>
              ) : (
                '—'
              )
            }
          />
          <Fact
            icon={Timer}
            label={resolvedMs ? 'Time to resolve' : 'Time in alarm'}
            tone={!resolvedMs && inAlarmMs > 15 * 60_000 ? 'danger' : undefined}
            value={fmtDuration(inAlarmMs)}
          />
        </div>

        {alarm.status === 'resolved' || alarm.status === 'false_alarm' ? (
          <div className="m-5 rounded-md border border-success/20 bg-success-soft px-4 py-3 text-sm text-success">
            Resolved {formatRelative(alarm.resolved_at)}
            {alarm.resolution_note ? ` — "${alarm.resolution_note}"` : ''}
          </div>
        ) : null}
      </div>

      <LocationPanel
        lat={alarm.trigger_lat}
        lng={alarm.trigger_lng}
        accuracy={alarm.trigger_accuracy_m}
        workerName={worker?.display_name}
      />

      {(alarm.status === 'active' || alarm.status === 'acknowledged') && (
        <ResolvePanel
          disabled={resolve.isPending}
          note={resolveNote}
          onNoteChange={setResolveNote}
          onResolve={(asFalseAlarm, outcome) =>
            resolve.mutate(
              { id: alarm.id, note: resolveNote || undefined, asFalseAlarm, outcome },
              {
                onSuccess: () => {
                  setResolveNote('');
                  navigate('/alarms');
                },
              },
            )
          }
        />
      )}

      <div className="rounded-lg bg-white p-6 shadow-panel">
        <h2 className="mb-4 font-heading text-lg font-semibold">Event timeline</h2>
        {events.length === 0 ? (
          <div className="text-sm text-ink-faint">No events recorded yet.</div>
        ) : (
          <ol className="relative space-y-4 border-l-2 border-line pl-6">
            {events.map((evt) => (
              <TimelineEntry key={evt.id} event={evt} actorName={nameFor(evt.actor_id)} />
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

// Worker identity + the primary duress action: call them. Phone comes from the
// roster (E.164); a tel: link dials straight from the monitor's device.
function WorkerHero({ worker, battery }: { worker?: RosterMember; battery: number | null }) {
  const name = worker?.display_name ?? 'Unknown worker';
  const meta = [worker?.job_title, worker?.state].filter(Boolean).join(' · ');
  const phone = worker?.phone_e164 ?? null;
  return (
    <div className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-lg border border-line bg-surface-1 px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-purple/10 text-sm font-bold text-brand-purple">
          {initials(name)}
        </div>
        <div>
          <div className="font-heading text-base font-bold text-ink">{name}</div>
          <div className="text-xs text-ink-faint">
            {meta || 'Worker'}
            {worker?.last_seen_at
              ? ` · last app activity ${formatRelative(worker.last_seen_at)}`
              : ''}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {battery != null && <BatteryChip pct={battery} />}
        {phone ? (
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700"
          >
            <Phone className="h-4 w-4" />
            Call {phone}
          </a>
        ) : (
          <span className="text-xs text-ink-faint">No phone on file</span>
        )}
      </div>
    </div>
  );
}

function BatteryChip({ pct }: { pct: number }) {
  const tone = pct < 15 ? 'text-danger' : pct < 30 ? 'text-warning' : 'text-ink-faint';
  return (
    <span
      className={cn('inline-flex items-center gap-1 text-xs font-medium', tone)}
      title="Device battery at trigger"
    >
      <Battery className="h-3.5 w-3.5" />
      {pct}%
    </span>
  );
}

// Where the worker was at panic — coordinates + a one-click path to a real map
// for dispatch. A Google Maps link is deliberately preferred over an embedded
// pin: the monitor can read the address, share it, or hand it to responders.
function LocationPanel({
  lat,
  lng,
  accuracy,
  workerName,
}: {
  lat: number | null;
  lng: number | null;
  accuracy: number | null;
  workerName?: string;
}) {
  const hasLoc = lat != null && lng != null;
  return (
    <div className="rounded-lg bg-white p-6 shadow-panel">
      <div className="mb-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-danger" />
        <h2 className="font-heading text-lg font-semibold">Where they triggered it</h2>
      </div>
      {hasLoc ? (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-sm text-ink">
            <span>
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </span>
            {accuracy != null && <span className="text-ink-faint">±{Math.round(accuracy)}m</span>}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <a
              href={`https://www.google.com/maps?q=${lat},${lng}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-purple/90"
            >
              <Navigation className="h-4 w-4" />
              Open in Google Maps
            </a>
            <Link
              to="/map"
              className="inline-flex items-center gap-1.5 rounded-md border border-line bg-surface-1 px-3 py-2 text-sm font-semibold text-ink-dim transition hover:border-brand-purple hover:text-brand-purple"
            >
              <MapPin className="h-4 w-4" />
              View on live map
            </Link>
          </div>
        </>
      ) : (
        <div className="text-sm text-ink-faint">
          No location was captured when this alarm triggered — the device had no GPS fix or location
          was off.{' '}
          <Link to="/map" className="text-brand-purple hover:underline">
            Check the live map
          </Link>{' '}
          for {workerName ? `${workerName}'s` : 'their'} last-known position.
        </div>
      )}
    </div>
  );
}

function Fact({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof Clock;
  label: string;
  value: ReactNode;
  tone?: 'danger' | 'warning';
}) {
  return (
    <div className="bg-surface-1 px-5 py-4">
      <div className="mb-1.5 flex items-center gap-1.5 eyebrow">
        <Icon className="h-3 w-3" />
        {label}
      </div>
      <div
        className={cn(
          'font-mono text-[13px] font-semibold',
          tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-ink',
        )}
      >
        {value}
      </div>
    </div>
  );
}

function ResolvePanel({
  disabled,
  note,
  onNoteChange,
  onResolve,
}: {
  disabled: boolean;
  note: string;
  onNoteChange: (v: string) => void;
  onResolve: (asFalseAlarm: boolean, outcome: AlarmResolutionOutcome) => void;
}) {
  // Default to "real incident" since that's the right answer when a monitor
  // hits "Resolve" after responding to an SOS. They can flip to "welfare ok"
  // for an overdue check-in or hit "Mark as false alarm" for an accidental
  // trigger. The picked value flows through to the alarm report so the
  // by-outcome column reflects reality.
  const [outcome, setOutcome] = useState<AlarmResolutionOutcome>('real_incident');
  return (
    <div className="rounded-lg bg-white p-6 shadow-panel">
      <h2 className="mb-2 font-heading text-lg font-semibold">Resolve</h2>
      <p className="mb-4 text-sm text-ink-faint">
        Add a short note describing the outcome. This appears on the timeline and the worker's
        incident history.
      </p>
      <textarea
        value={note}
        onChange={(e) => onNoteChange(e.target.value)}
        placeholder="e.g. Contacted worker — all clear. Vehicle breakdown, not safety incident."
        rows={3}
        className="w-full resize-none rounded-md border border-line-strong px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple"
      />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wider text-ink-faint">
          Outcome:
        </span>
        {(
          [
            { value: 'real_incident' as const, label: 'Real incident' },
            { value: 'welfare_check_ok' as const, label: 'Welfare check OK' },
          ] satisfies { value: AlarmResolutionOutcome; label: string }[]
        ).map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => setOutcome(opt.value)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs font-medium transition',
              outcome === opt.value
                ? 'border-brand-purple bg-brand-purple text-white'
                : 'border-line bg-white text-ink-dim hover:border-line-strong',
            )}
          >
            {opt.label}
          </button>
        ))}
      </div>
      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onResolve(true, 'false_alarm')}
          disabled={disabled}
          className="rounded-md border border-line-strong bg-white px-4 py-2 text-sm font-medium text-ink-dim transition hover:bg-surface-3 disabled:opacity-50"
        >
          Mark as false alarm
        </button>
        <button
          type="button"
          onClick={() => onResolve(false, outcome)}
          disabled={disabled}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-emerald-700 disabled:opacity-50"
        >
          {disabled ? 'Resolving…' : 'Resolve'}
        </button>
      </div>
    </div>
  );
}

function TimelineEntry({ event, actorName }: { event: AlarmEventDto; actorName: string | null }) {
  return (
    <li className="relative">
      <span className="absolute -left-[30px] top-1.5 h-3 w-3 rounded-full border-2 border-white bg-brand-purple shadow" />
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-sm font-medium text-ink">
            {prettifyEventType(event.event_type)}
            {actorName ? <span className="font-normal text-ink-faint"> · {actorName}</span> : null}
          </div>
          {event.payload?.note ? (
            <div className="mt-0.5 text-sm text-ink-faint">“{String(event.payload.note)}”</div>
          ) : null}
        </div>
        <div className="shrink-0 text-xs text-ink-faint" title={formatDateTime(event.occurred_at)}>
          {formatRelative(event.occurred_at)}
        </div>
      </div>
    </li>
  );
}

// milliseconds → "45s" / "17m 3s" / "2h 5m"
function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) {
    const rem = s % 60;
    return rem ? `${m}m ${rem}s` : `${m}m`;
  }
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return mm ? `${h}h ${mm}m` : `${h}h`;
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join('')
      .toUpperCase() || '?'
  );
}

function prettifyEventType(type: string): string {
  const map: Record<string, string> = {
    alarm_triggered: 'Alarm triggered',
    monitor_acknowledged: 'Monitor acknowledged',
    monitor_resolved: 'Monitor resolved',
    flagged_false_alarm: 'Flagged as false alarm',
  };
  return map[type] ?? type.replace(/_/g, ' ');
}

function Loading() {
  return (
    <div className="mx-auto max-w-4xl animate-pulse space-y-6">
      <div className="h-6 w-24 rounded bg-gray-200" />
      <div className="h-40 rounded-lg bg-gray-200" />
      <div className="h-60 rounded-lg bg-gray-200" />
    </div>
  );
}

function NotFound() {
  return (
    <div className="mx-auto max-w-md rounded-lg bg-white p-8 text-center shadow-panel">
      <h2 className="mb-2 font-heading text-lg font-semibold">Alarm not found</h2>
      <p className="mb-4 text-sm text-ink-faint">
        It may have been in a different organisation, or the ID is invalid.
      </p>
      <Link
        to="/alarms"
        className="inline-flex items-center gap-1 text-sm text-brand-purple hover:underline"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to alarms
      </Link>
    </div>
  );
}
